/* eslint-disable @nx/enforce-module-boundaries */

'use client';

import { RemindersBell } from '@mybridge/icons';
import { Flex, Switch, Text, useToast } from '@mybridge/ui';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState, useMemo } from 'react';
import { AuthenticatedProviders } from 'v4/snippets/providers/authenticated';
import API from 'v4/store/common/api';
import { APIEndPoints } from 'v4/store/common/endPoint';

const AllToggle = ({ module, settings, onChange, onToggleChange }) => {
  const { t } = useTranslator();
  const toast = useToast({ position: 'top', isClosable: true });
  const [isTrue, setIsTrue] = useState(false);

  // Generate unique switch ID
  const switchId = useMemo(() => `all-${module}`, [module]);

  // Determine switch state based on API data ONLY
  useEffect(() => {
    const { pushSettings, emailSettings, smsSettings } = settings ?? {};
    
    // Get the latest setting for each notification type for this module (excluding company pages)
    const getLatestSetting = (settingsArray) => {
      if (!settingsArray || !Array.isArray(settingsArray) || settingsArray.length === 0) return null;
      
      const moduleSettings = settingsArray.filter(
        item => item._for === module && !item.company_page
      );
      
      if (moduleSettings.length === 0) return null;
      
      // Sort by updated_at in descending order and get the first (most recent) one
      const sortedSettings = moduleSettings.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at);
        const dateB = new Date(b.updated_at || b.created_at);
        return dateB - dateA; // Descending order (newest first)
      });
      
      return sortedSettings[0];
    };

    const latestPush = getLatestSetting(pushSettings);
    const latestEmail = getLatestSetting(emailSettings);
    const latestSms = getLatestSetting(smsSettings);

    // AllToggle should be ON only when ALL notification types are enabled
    // If any notification type is missing or disabled, AllToggle should be OFF
    const pushEnabled = latestPush?.enabled || false;
    const emailEnabled = latestEmail?.enabled || false;
    const smsEnabled = latestSms?.enabled || false;
    
    // ALL must be enabled for AllToggle to be ON
    const allEnabled = pushEnabled && emailEnabled && smsEnabled;
    
    setIsTrue(allEnabled);
  }, [settings, module]);

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async (body) => {
      // Make all three API calls simultaneously
      return Promise.all([
        API({
          method: 'POST',
          url: `${APIEndPoints.Notification}/push/`,
          body: JSON.stringify(body),
        }),
        API({
          method: 'POST',
          url: `${APIEndPoints.Notification}/email/`,
          body: JSON.stringify(body),
        }),
        API({
          method: 'POST',
          url: `${APIEndPoints.Notification}/sms/`,
          body: JSON.stringify(body),
        }),
      ]);
    },
  });

  const onChange_ = async () => {
    const newState = !isTrue;
    
    try {
      // Update all notification types for this module
      await mutateAsync({ 
        _for: module, 
        enabled: newState 
      });
      
      // Notify parent component about the change
      if (onToggleChange) {
        onToggleChange(newState);
      }
      
      // Refetch data to get updated state from API
      if (onChange) {
        await onChange();
      }
      
      toast({
        title: t('toast.success') || "Success",
        description: `${t('notification.allNotifications') || "All notifications"} ${newState ? (t('notification.enabled') || "enabled") : (t('notification.disabled') || "disabled")} ${t('notification.for')} ${t(`notification.${module}`)}`,
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      console.error("Error toggling notification settings:", error);
      
      toast({
        title: "Error",
        description: "Failed to update notification settings",
        status: "error",
        duration: 3000,
      });
    }
  };

  return (
    <AuthenticatedProviders>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        py="15px"
        borderBottom="1px"
        borderColor="brandGray.500"
      >
        <Flex>
          <RemindersBell width="24" height="24" color="#68A4D1"/>
          <Text ms="10px" color="brandGray.850">
            {t('settingNotification.allowNotifications') || "Allow notifications on myBridge"}
          </Text>
        </Flex>
        <Switch
          name={switchId}
          label="All Notifications"
          value={isTrue}
          isChecked={isTrue}
          onChange={onChange_}
          isDisabled={isLoading}
        />
      </Flex>
    </AuthenticatedProviders>
  );
};

export default AllToggle;
